import React, { useState, useEffect, useContext, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeContext } from '../../context/ThemeContext';
import { formatExtra } from '../../constants/categorias';
import { API_URL } from '../../utils/api';
import { getContrastColor } from '../../utils/colorUtils';
import AdaptiveGrid from '../../components/ImageGrid/AdaptiveGrid';
import DetailGrid from '../../components/ImageGrid/DetailGrid';
import {
  ContainerBuscador, FiltroSep, SearchBox, SearchButton, VistaBtn,
} from '../../components/Formularios/Buscador/Buscador.styles';
import {
  PageWrapper, HeaderCard, UserInfo, Avatar, UserMeta, UserName, UserSub,
  HeaderRight, CountBadge, AddBtn, ToggleBtn,
  Grid, FlatGrid, FlatCard, FlatImg, FlatInfo,
  FlatPrice, FlatNombre, FlatDesc, FlatExtras, FlatBadges, FlatBadge,
  CardPrice, CardDesc, CardExtras,
  CardOuter, CardWrapper, CardActions, ActionBtn,
  EmptyState, EmptyIcon, EmptyTitle, EmptySub, ProtectedScreen,
  Overlay, ConfirmBox, ConfirmTitle, ConfirmActions,
  DetailBox, DetailScroll, DetailClose, DetailTitle, DetailActions, DetailPrice,
  DetailMeta, MetaTag, DetailDesc, ShareRow, ShareLabel, ShareBtn,
  LightboxOverlay, LightboxImg, LightboxNav, LightboxClose, LightboxDots, LightboxDot,
  VendidoRibbon,
} from './MiTrastero.styles';

const ModalSubir  = lazy(() => import('../../components/Modal/ModalSubir/ModalSubir'));
const ModalEditar = lazy(() => import('../../components/Modal/ModalEditar/ModalEditar'));

// Construye la URL de una imagen servida por el backend, sin duplicar el
// dominio si la ruta ya viene absoluta (y corrigiendo el host si viene mal).
// Placeholder neutro mientras se confirma si hay logo propio (evita el parpadeo)
const DEFAULT_AVATAR_SKELETON = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' rx='10' fill='%23333'/%3E%3C/svg%3E`;

function buildBackendImgUrl(path) {
  if (!path) return '';
  if (/^data:/i.test(path)) return path;
  if (/^https?:\/\//i.test(path)) {
    try {
      const u = new URL(path);
      const api = new URL(API_URL);
      u.protocol = api.protocol;
      u.host = api.host;
      return u.toString();
    } catch {
      return path;
    }
  }
  return `${API_URL}${path}`;
}

// #trasterocard — sub-componente tarjeta agrupada (título + AdaptiveGrid + hover overlay)
function TrasteroCard({ task, onDelete, onOpen, onEdit, onRenovar, theme }) {
  const acc = theme.accent;
  const bg  = theme.modalBg;
  const txt = getContrastColor(bg);
  const precio = task.Precio !== null && task.Precio !== undefined
    ? task.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' €'
    : null;
  const extra = formatExtra(task.Extras);
  const titleBg  = theme.cardTitle || '#1a1a2e';
  const titleTxt = getContrastColor(titleBg);

  let tooltipVigencia = '';
  if (task.FechaVencimiento) {
    const hasta = new Date(task.FechaVencimiento);
    const diasRestantes = Math.ceil((hasta - Date.now()) / (1000 * 60 * 60 * 24));
    const fechaStr = hasta.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
    if (diasRestantes <= 0) tooltipVigencia = `❌ Venció el ${fechaStr}`;
    else if (diasRestantes <= 15) tooltipVigencia = `⚠️ Vence en ${diasRestantes} días · ${fechaStr}`;
    else tooltipVigencia = `✅ Vence ${fechaStr}`;
  }

  return (
    <CardOuter data-tooltip={tooltipVigencia || undefined}>
    <CardWrapper bg={bg} accent={acc} onClick={() => onOpen(task)}>
      {task.vendido && <VendidoRibbon>VENDIDO</VendidoRibbon>}
      {/* ── Barra de título ── */}
      <div style={{
        background: titleBg, color: titleTxt,
        height: 54, display: 'flex', alignItems: 'center',
        padding: '0 8px', gap: 6,
        fontSize: 15, fontWeight: 700,
        borderRadius: '8px 8px 0 0',
      }}>
        <img
          src={`${buildBackendImgUrl(task.Ruta)}/${task.Thumb1 || task.Imagen1}`}
          alt=""
          style={{ width: 44, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {task.Nombre}
        </span>
      </div> 

      {/* ── Grid de imágenes con overlay hover ── */}
      <div style={{ position: 'relative' }}>
        <AdaptiveGrid
          ruta={buildBackendImgUrl(task.Ruta)}
          imgs={[task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]}
          thumbs={[task.Thumb1||task.Imagen1, task.Thumb2||task.Imagen2, task.Thumb3||task.Imagen3, task.Thumb4||task.Imagen4]}
          width="100%"
        />
        <CardActions className="card-actions">
          {tooltipVigencia.startsWith('❌') && (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <ActionBtn accent={acc} onClick={e => { e.stopPropagation(); onRenovar(task); }}>🔄 Renovar</ActionBtn>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            <ActionBtn accent={acc} onClick={e => { e.stopPropagation(); onEdit(task); }}>✏️ Editar</ActionBtn>
            <ActionBtn danger onClick={e => { e.stopPropagation(); onDelete(task); }}>🗑️ Eliminar</ActionBtn>
          </div>
        </CardActions>
      </div>

      {/* ── Datos del artículo ── */}
      <FlatInfo>
        {precio && <CardPrice accent={acc}>{precio}</CardPrice>}
        {task.Descripcion && <CardDesc color={txt}>{task.Descripcion}</CardDesc>}
        {extra && <CardExtras color={txt}>{extra}</CardExtras>}
        {(task.AceptaCambio || task.Negociable) && (
          <FlatBadges>
            {task.AceptaCambio && <FlatBadge accent={acc}>Acepta cambio</FlatBadge>}
            {task.Negociable   && <FlatBadge accent={acc}>Negociable</FlatBadge>}
          </FlatBadges>
        )}
      </FlatInfo>
    </CardWrapper>
    </CardOuter>
  );
}

// #component — estado, theme, refs
function MiTrastero({ user }) {
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const bg  = theme.modalBg;
  const acc = theme.accent;

  const [trasteros,       setTrasteros]       = useState([]);
  const [trasteroId,      setTrasteroId]      = useState(null);
  const [vigencia,        setVigencia]        = useState(null);
  const [cargando,        setCargando]        = useState(true);
  const [confirmar,       setConfirmar]       = useState(null);
  const [detalle,         setDetalle]         = useState(null);
  const [showSubir,       setShowSubir]       = useState(false);
  const [vistaPlana,      setVistaPlana]      = useState(false);
  const [editar,          setEditar]          = useState(null);
  const [confirmarImagen, setConfirmarImagen] = useState(null);
  const [lightbox,        setLightbox]        = useState(null);
  const [igCopied,        setIgCopied]        = useState(false);
  const [trasteroLogo,    setTrasteroLogo]    = useState(null);
  const [logoCargado,     setLogoCargado]     = useState(false);
  const [busqueda,        setBusqueda]        = useState('');

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('trastero_token');
    fetch(`${API_URL}/api/auth/foto-trastero`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.foto_trastero) setTrasteroLogo(d.foto_trastero); })
      .catch(() => {})
      .finally(() => setLogoCargado(true));
  }, [user]);

  const handleCopyIg = (url) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setIgCopied(true);
    setTimeout(() => setIgCopied(false), 2000);
  };

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === 'Escape')      setLightbox(null);
      if (e.key === 'ArrowRight')  setLightbox(p => p.idx < p.imgs.length - 1 ? { ...p, idx: p.idx + 1 } : p);
      if (e.key === 'ArrowLeft')   setLightbox(p => p.idx > 0 ? { ...p, idx: p.idx - 1 } : p);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  // #fetch-datos — carga trasteroId + artículos al montar
  useEffect(() => {
    if (!user) return;
    setCargando(true);
    fetch(`${API_URL}/api/trasteros/contenedor?usuario_id=${user.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('trastero_token')}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (data.length > 0) {
          setTrasteroId(data[0].id);
          setVigencia(data[0]);
        }
      })
      .catch(() => {});
    fetch(`${API_URL}/api/trasteros?usuario_id=${user.id}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setTrasteros(data))
      .catch(() => setTrasteros([]))
      .finally(() => setCargando(false));
  }, [user]);

  // Bloquear scroll del body cuando hay modal abierto
  useEffect(() => {
    const hayModal = !!detalle || !!editar;
    document.body.style.overflow = hayModal ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [detalle, editar]);

  if (!user) {
    return (
      <PageWrapper bg={theme.background || '#000000'}>
        <ProtectedScreen>
          <div style={{ fontSize: 56 }}>🔒</div>
          <h2 style={{ color: getContrastColor(theme.background), margin: 0 }}>Acceso restringido</h2>
          <p style={{ color: getContrastColor(theme.background) + '88', margin: 0 }}>
            Inicia sesión para ver tu trastero
          </p>
          <AddBtn accent={acc} onClick={() => navigate('/')}>Ir al inicio</AddBtn>
        </ProtectedScreen>
      </PageWrapper>
    );
  }

  // #handlers — eliminar artículo, eliminar imagen
  const handleEliminar = async (trastero) => {
    setConfirmar(null);
    setDetalle(null);
    try {
      await fetch(`${API_URL}/api/trasteros/${trastero.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('trastero_token')}` },
      });
    } catch {}
    setTrasteros(prev => prev.filter(t => t.id !== trastero.id));
  };

  const handleRenovar = async (task) => {
    const token = localStorage.getItem('trastero_token');
    try {
      const res = await fetch(`${API_URL}/api/trasteros/${task.id}/renovar`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setTrasteros(prev => prev.map(t =>
        t.id === task.id ? { ...t, FechaVencimiento: data.fecha_vencimiento } : t
      ));
    } catch {}
  };

  const handleEliminarImagen = async ({ task, posicion }) => {
    setConfirmarImagen(null);
    try {
      const res = await fetch(`${API_URL}/api/trasteros/${task.id}/imagenes/${posicion}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('trastero_token')}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.deleted) {
        setTrasteros(prev => prev.filter(t => t.id !== task.id));
      } else {
        setTrasteros(prev => prev.map(t => t.id === data.id ? data : t));
      }
    } catch {}
  };

  const trasterosFiltrados = busqueda.trim()
    ? trasteros.filter(t =>
        t.Nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        t.Descripcion?.toLowerCase().includes(busqueda.toLowerCase())
      )
    : trasteros;

  // #jsx-render — header, grid/flatgrid, modales, lightbox
  return (
    <PageWrapper bg={theme.background || '#000000'}>
      <Helmet>
        <title>{user?.name ? `${user.name} · Mi Trastero` : 'Mi Trastero · Trastero'}</title>
        <meta name="description" content="Gestiona tus artículos publicados, añade nuevas publicaciones y controla tu trastero." />
      </Helmet>

      {/* ── Modal subir artículo ── */}
      <Suspense fallback={null}>
        <ModalSubir
          isOpen={showSubir}
          onClose={() => setShowSubir(false)}
          onPublicado={nuevo => setTrasteros(prev => [...prev, nuevo])}
          trasteroId={trasteroId}
        />
      </Suspense>

      {/* ── Modal editar artículo ── */}
      <Suspense fallback={null}>
        <ModalEditar
          isOpen={!!editar}
          trastero={editar}
          onClose={() => setEditar(null)}
          onGuardado={actualizado => {
            setTrasteros(prev => prev.map(t => t.id === actualizado.id ? actualizado : t));
            setEditar(null);
          }}
        />
      </Suspense>

      {/* ── Modal detalle ── */}
      {detalle && (
        <Overlay onClick={() => setDetalle(null)}>
          <DetailBox bg={bg} onClick={e => e.stopPropagation()}>
            <DetailClose onClick={() => setDetalle(null)}>✕</DetailClose>
            <DetailScroll>
            <DetailTitle color={getContrastColor(bg)}>{detalle.Nombre}</DetailTitle>

            {detalle.Precio !== null && detalle.Precio !== undefined && (
              <DetailPrice accent={acc}>
                {detalle.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 })} €
              </DetailPrice>
            )}
            {/*  BADGES — al pie */}
            {(detalle.Categoria || detalle.Subcategoria || detalle.Negociable || detalle.AceptaCambio || formatExtra(detalle.Extras)) && (
              <DetailMeta>
                {detalle.Subcategoria && <MetaTag accent={acc}>{detalle.Subcategoria}</MetaTag>}
                {!detalle.Subcategoria && detalle.Categoria && <MetaTag accent={acc}>{detalle.Categoria}</MetaTag>}
                {formatExtra(detalle.Extras) && <MetaTag>{formatExtra(detalle.Extras)}</MetaTag>}
                {detalle.Negociable   && <MetaTag accent={acc}>Negociable</MetaTag>}
                {detalle.AceptaCambio && <MetaTag accent={acc}>Acepta cambio</MetaTag>}
                {detalle.RedesPublicadas?.includes('instagram') && <MetaTag accent={acc}>📷 Instagram</MetaTag>}
              </DetailMeta>
            )}

            {detalle.Descripcion && (
              <DetailDesc color={getContrastColor(bg)}>{detalle.Descripcion}</DetailDesc>
            )}

            <DetailGrid
              ruta={buildBackendImgUrl(detalle.Ruta)}
              imgs={[detalle.Imagen1, detalle.Imagen2, detalle.Imagen3, detalle.Imagen4]}
              onImgClick={i => {
                const allImgs = [detalle.Imagen1, detalle.Imagen2, detalle.Imagen3, detalle.Imagen4]
                  .filter(Boolean).map(n => `${buildBackendImgUrl(detalle.Ruta)}/${n}`);
                setLightbox({ imgs: allImgs, idx: i });
              }}
            />

            <DetailActions>
              {/* Iconos compartir — izquierda */}
              {(() => {
                const base    = window.location.origin;
                const pageUrl = vigencia?.nombre ? `${base}/de/${encodeURIComponent(vigencia.nombre)}` : base;
                const url     = encodeURIComponent(pageUrl);
                const texto   = encodeURIComponent(`${detalle.Nombre}${detalle.Precio ? ` — ${detalle.Precio} €` : ''} | trasteroweb.es`);
                return (
                  <ShareRow style={{ paddingTop: 0, borderTop: 'none', margin: 0 }}>
                    <ShareBtn as="button" onClick={() => handleCopyIg(pageUrl)} accent={acc} data-tooltip={igCopied ? '¡Enlace copiado!' : 'Copiar enlace para Instagram'} style={{ cursor: 'pointer', border: 'none' }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                    </ShareBtn>
                    <ShareBtn href={`https://twitter.com/intent/tweet?text=${texto}&url=${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en X">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.912-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </ShareBtn>
                    <ShareBtn href={`https://wa.me/?text=${texto}%20${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en WhatsApp">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.105.549 4.082 1.504 5.798L.057 23.998l6.337-1.423A11.937 11.937 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.804 9.804 0 01-4.99-1.366l-.358-.213-3.76.845.9-3.671-.233-.377A9.818 9.818 0 012.182 12C2.182 6.58 6.58 2.182 12 2.182S21.818 6.58 21.818 12 17.42 21.818 12 21.818z"/></svg>
                    </ShareBtn>
                    <ShareBtn href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" accent={acc} data-tooltip="Compartir en Facebook">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </ShareBtn>
                  </ShareRow>
                );
              })()}
              {/* Acciones — derecha */}
              <div style={{ display: 'flex', gap: 10 }}>
                <ActionBtn accent={acc} onClick={() => { setDetalle(null); setEditar(detalle); }}>✏️ Editar</ActionBtn>
                <ActionBtn danger onClick={() => { setDetalle(null); setConfirmar(detalle); }}>🗑️ Eliminar</ActionBtn>
              </div>
            </DetailActions>
            </DetailScroll>
          </DetailBox>
        </Overlay>
      )}

      {/* ── Confirmar eliminación ── */}
      {confirmar && (
        <Overlay onClick={() => setConfirmar(null)}>
          <ConfirmBox bg={bg} onClick={e => e.stopPropagation()}>
            <ConfirmTitle bg={bg}>
              ¿Eliminar "{confirmar.Nombre}"?<br />
              <span style={{ fontWeight: 400, fontSize: 14, opacity: 0.7 }}>
                Esta acción no se puede deshacer.
              </span>
            </ConfirmTitle>
            <ConfirmActions>
              <ActionBtn danger onClick={() => handleEliminar(confirmar)}>Sí, eliminar</ActionBtn>
              <ActionBtn accent={acc} onClick={() => setConfirmar(null)}>Cancelar</ActionBtn>
            </ConfirmActions>
          </ConfirmBox>
        </Overlay>
      )}

      {/* ── Confirmar eliminar imagen ── */}
      {confirmarImagen && (
        <Overlay onClick={() => setConfirmarImagen(null)}>
          <ConfirmBox bg={bg} onClick={e => e.stopPropagation()}>
            <ConfirmTitle bg={bg}>
              ¿Eliminar esta imagen de "{confirmarImagen.task.Nombre}"?<br />
              <span style={{ fontWeight: 400, fontSize: 14, opacity: 0.7 }}>
                {[confirmarImagen.task.Imagen1, confirmarImagen.task.Imagen2,
                  confirmarImagen.task.Imagen3, confirmarImagen.task.Imagen4].filter(Boolean).length === 1
                  ? 'Es la única imagen — se eliminará el artículo completo.'
                  : 'Solo se borrará esta imagen.'}
              </span>
            </ConfirmTitle>
            <ConfirmActions>
              <ActionBtn danger onClick={() => handleEliminarImagen(confirmarImagen)}>Sí, eliminar</ActionBtn>
              <ActionBtn accent={acc} onClick={() => setConfirmarImagen(null)}>Cancelar</ActionBtn>
            </ConfirmActions>
          </ConfirmBox>
        </Overlay>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <LightboxOverlay onClick={() => setLightbox(null)}>
          <LightboxClose onClick={() => setLightbox(null)}>✕</LightboxClose>
          <LightboxNav prev disabled={lightbox.idx === 0}
            onClick={e => { e.stopPropagation(); setLightbox(p => ({ ...p, idx: p.idx - 1 })); }}>
            ‹
          </LightboxNav>
          <LightboxImg src={lightbox.imgs[lightbox.idx]} alt="" onClick={e => e.stopPropagation()} />
          <LightboxNav disabled={lightbox.idx === lightbox.imgs.length - 1}
            onClick={e => { e.stopPropagation(); setLightbox(p => ({ ...p, idx: p.idx + 1 })); }}>
            ›
          </LightboxNav>
          {lightbox.imgs.length > 1 && (
            <LightboxDots onClick={e => e.stopPropagation()}>
              {lightbox.imgs.map((_, i) => (
                <LightboxDot key={i} active={i === lightbox.idx}
                  onClick={() => setLightbox(p => ({ ...p, idx: i }))} />
              ))}
            </LightboxDots>
          )}
        </LightboxOverlay>
      )}

      {/* ── Header ── */}
      <HeaderCard bg={bg} accent={acc}>
        <UserInfo>
          {logoCargado
            ? <Avatar src={buildBackendImgUrl(trasteroLogo) || buildBackendImgUrl(user.avatar) || user.avatar} alt={user.name} accent={acc} />
            : <Avatar src={DEFAULT_AVATAR_SKELETON} alt="" accent={acc} style={{ opacity: 0.3 }} />
          }
          <UserMeta>
            <UserName bg={bg}>{vigencia?.nombre || 'Mi Trastero'}</UserName>
          </UserMeta>
        </UserInfo>
        {!cargando && trasteros.length > 0 && (
          <ContainerBuscador style={{ flex: 1, maxWidth: 480 }}>
            <VistaBtn
              type="button"
              active={vistaPlana}
              onClick={() => setVistaPlana(v => !v)}
              title={vistaPlana ? 'Ver en grupo' : 'Ver todas las imágenes'}
            >
              {vistaPlana ? '⊞' : '▦'}
            </VistaBtn>
            <FiltroSep />
            <SearchBox
              type="text"
              placeholder={`Buscar en ${vigencia?.nombre || 'Mi Trastero'}...`}
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
            <SearchButton type="button" onClick={() => setBusqueda('')}>
              {busqueda ? '✕' : '🔍'}
            </SearchButton>
          </ContainerBuscador>
        )}
        <HeaderRight>
          {/* Badge de vigencia */}
          {vigencia && (() => {
            const hasta = vigencia.vigencia_hasta ? new Date(vigencia.vigencia_hasta) : null;
            const diasRestantes = hasta ? Math.ceil((hasta - Date.now()) / (1000 * 60 * 60 * 24)) : 0;
            const fechaStr = hasta ? hasta.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
            if (!vigencia.activo || diasRestantes <= 0) return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  title="Al reactivar tu trastero aparecerá nuevamente en la página principal de búsqueda"
                  style={{ fontSize: 12, fontWeight: 600, color: '#e74c3c', background: 'rgba(231,76,60,0.12)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: 20, padding: '4px 10px', cursor: 'help' }}>
                  ❌ Trastero inactivo
                </span>
                <button
                  title="Al reactivar tu trastero aparecerá nuevamente en la página principal de búsqueda"
                  onClick={async () => {
                    const token = localStorage.getItem('trastero_token');
                    const res = await fetch(`${API_URL}/api/trasteros/contenedor/${vigencia.id}/reactivar`, {
                      method: 'PATCH',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                    if (res.ok) {
                      const data = await res.json();
                      setVigencia(v => ({ ...v, ...data }));
                    }
                  }}
                  style={{ fontSize: 12, fontWeight: 600, color: '#fff', background: acc, border: 'none', borderRadius: 20, padding: '4px 12px', cursor: 'pointer' }}>
                  🔄 Reactivar
                </button>
              </div>
            );
            if (diasRestantes <= 15) return (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#f39c12', background: 'rgba(243,156,18,0.12)', border: '1px solid rgba(243,156,18,0.3)', borderRadius: 20, padding: '4px 10px' }}>
                ⚠️ Vence en {diasRestantes} días · {fechaStr}
              </span>
            );
            return (
              <span style={{ fontSize: 12, fontWeight: 600, color: '#2ecc71', background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: 20, padding: '4px 10px' }}>
                ✅ Activo · Vence {fechaStr}
              </span>
            );
          })()}
          {!cargando && (
            <CountBadge bg={bg} accent={acc}>
              📦 {trasteros.length} {trasteros.length === 1 ? 'artículo' : 'artículos'}
            </CountBadge>
          )}
          <AddBtn accent={acc} onClick={() => setShowSubir(true)}>+ Añadir</AddBtn>
        </HeaderRight>
      </HeaderCard>

{/* ── Contenido ── */}
      {cargando ? (
        <EmptyState>
          <EmptyIcon>⏳</EmptyIcon>
          <EmptyTitle bg={theme.background}>Cargando...</EmptyTitle>
        </EmptyState>
      ) : trasteros.length === 0 ? (
        <EmptyState>
          <EmptyIcon>📦</EmptyIcon>
          <EmptyTitle bg={theme.background}>Aún no tienes artículos</EmptyTitle>
          <EmptySub bg={theme.background}>Publica tu primer trastero y empieza a vender</EmptySub>
          <AddBtn accent={acc} onClick={() => setShowSubir(true)}>+ Publicar mi primer trastero</AddBtn>
        </EmptyState>
      ) : vistaPlana ? (
        <FlatGrid>
          {trasterosFiltrados.flatMap(task =>
            [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]
              .filter(Boolean)
              .map((img, i) => {
                const bg2 = theme.modalBg || '#1a1a1a';
                const txt = getContrastColor(bg2);
                const precio = task.Precio !== null && task.Precio !== undefined
                  ? task.Precio.toLocaleString('es-ES', { minimumFractionDigits: 0 }) + ' €'
                  : null;
                const extra = formatExtra(task.Extras);
                const allImgs = [task.Imagen1, task.Imagen2, task.Imagen3, task.Imagen4]
                  .filter(Boolean).map(n => `${buildBackendImgUrl(task.Ruta)}/${n}`);
                return (
                  <FlatCard key={`${task.id}-${i}`} bg={bg2} accent={acc}
                    onClick={() => setLightbox({ imgs: allImgs, idx: i })}>
                    <div style={{ position: 'relative' }}>
                      <FlatImg src={`${buildBackendImgUrl(task.Ruta)}/${img}`} alt={task.Nombre} loading="lazy" />
                      <CardActions className="card-actions">
                        <ActionBtn accent={acc} onClick={e => { e.stopPropagation(); setEditar(task); }}>✏️ Editar</ActionBtn>
                        <ActionBtn danger onClick={e => { e.stopPropagation(); setConfirmarImagen({ task, posicion: i + 1 }); }}>🗑️ Eliminar</ActionBtn>
                      </CardActions>
                    </div>
                    <FlatInfo>
                      {precio && <FlatPrice accent={acc}>{precio}</FlatPrice>}
                      <FlatNombre color={txt}>{task.Nombre}</FlatNombre>
                      {task.Descripcion && <FlatDesc color={txt}>{task.Descripcion}</FlatDesc>}
                      {extra && <FlatExtras color={txt}>{extra}</FlatExtras>}
                        {(task.AceptaCambio || task.Negociable || task.RedesPublicadas?.includes('instagram')) && (
                          <FlatBadges>
                            {task.AceptaCambio && <FlatBadge accent={acc}>Acepta cambio</FlatBadge>}
                            {task.Negociable   && <FlatBadge accent={acc}>Negociable</FlatBadge>}
                            {task.RedesPublicadas?.includes('instagram') && <FlatBadge accent={acc}>📷 Instagram</FlatBadge>}
                          </FlatBadges>
                        )}
                    </FlatInfo>
                  </FlatCard>
                );
              })
          )}
        </FlatGrid>
      ) : (
        <Grid>
          {trasterosFiltrados.map(task => (
            <TrasteroCard
              key={task.id || task.Nombre}
              task={task}
              theme={theme}
              onDelete={t => setConfirmar(t)}
              onOpen={t => {
                if (window.innerWidth <= 600) {
                  const allImgs = [t.Imagen1, t.Imagen2, t.Imagen3, t.Imagen4]
                    .filter(Boolean)
                    .map(n => `${buildBackendImgUrl(t.Ruta)}/${n}`);
                  setLightbox({ imgs: allImgs, idx: 0 });
                } else {
                  setDetalle(t);
                }
              }}
              onEdit={t => setEditar(t)}
              onRenovar={handleRenovar}
            />
          ))}
        </Grid>
      )}

    </PageWrapper>
  );
}

export default MiTrastero;